// Strip data-layer transaction payloads from canvas elements before persisting.
//
// `expanded_transactions`, `token_transfers` and `internal_txs` are DATA-LAYER
// state owned by useTransactionStore (CLAUDE.md R12). They are refetched /
// rebuilt on load (Node Inspector re-hydrates on node re-expand), so they must
// never be baked into a persisted snapshot. Leaving them in bloats graph_state
// ~35x (measured: 3.7 MB of expanded_transactions = 69% of one 5.4 MB snapshot),
// which both blows sessionStorage's ~5 MB quota AND makes the server snapshot so
// large that the reload GET aborts on the 30 s axios timeout — the canvas then
// shows the empty placeholder ("blank screen on reopen").
//
// This is the single source of truth for that strip, used by BOTH the
// sessionStorage persist (useGraphStore partialize) and the server save
// (Workbench.executeSave) so the two paths can never drift out of symmetry —
// the asymmetry was the original bug (sessionStorage stripped, server save did
// not).

const DATA_LAYER_KEYS = ['expanded_transactions', 'token_transfers', 'internal_txs'] as const;

function stripElement(el: any): any {
    const data = el?.data as any;
    if (!data) return el;
    if (
        data.expanded_transactions === undefined &&
        data.token_transfers === undefined &&
        data.internal_txs === undefined
    ) {
        return el;
    }
    // Preserve a lightweight count so the Node Inspector can show
    // "N transactions — re-expand to load" without the heavy payload.
    const txCount = Array.isArray(data.expanded_transactions)
        ? data.expanded_transactions.length
        : undefined;
    const {
        expanded_transactions: _t,
        token_transfers: _tt,
        internal_txs: _it,
        ...lean
    } = data;
    if (txCount !== undefined && lean.total_expanded_txs === undefined) {
        lean.total_expanded_txs = txCount;
    }
    return { ...el, data: lean };
}

/** Array form — used by useGraphStore's sessionStorage partialize. */
export function stripDataLayer(elements: any[]): any[] {
    return (elements || []).map(stripElement);
}

/**
 * In-place {nodes, edges} form — used by Workbench.executeSave on the
 * `cy.json().elements` clone (matches normalizeVisibilityForSerialize's shape).
 * Mutates the passed object so it can be chained after visibility normalization.
 */
export function stripDataLayerInPlace(elements: { nodes?: any[]; edges?: any[] } | undefined): void {
    if (!elements) return;
    if (Array.isArray(elements.nodes)) elements.nodes = elements.nodes.map(stripElement);
    if (Array.isArray(elements.edges)) elements.edges = elements.edges.map(stripElement);
}
